import { ThemeEvents } from '@theme/events';

/** @type {Map<string, string>} */
const activeRegions = new Map();

/**
 * @param {Element} tabs
 * @param {string} region
 */
function applyRegion(tabs, region) {
  const container = tabs.closest('fieldset, .variant-option--has-region-tabs');
  if (!container) return;

  tabs.querySelectorAll('[data-size-region]').forEach((button) => {
    const isActive = button.dataset.sizeRegion === region;
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });

  container.querySelectorAll('[data-size-label-eu]').forEach((element) => {
    const value = element.getAttribute(`data-size-label-${region}`);
    if (value) element.textContent = value;
  });
}

/**
 * Patch region tabs and labels on incoming HTML before morph — prevents EU flash.
 * @param {Element | null | undefined} root
 * @param {string} productId
 */
export function patchRegionLabels(root, productId) {
  if (!root || !productId || !activeRegions.has(productId)) return;

  const region = activeRegions.get(productId);
  if (!region) return;

  root.querySelectorAll('[data-size-region-tabs]').forEach((tabs) => {
    applyRegion(tabs, region);
  });
}

/**
 * @param {Document} html
 * @param {string} productId
 */
export function patchStickyVariantInHtml(html, productId) {
  if (!activeRegions.has(productId)) return;

  const picker = html.querySelector(`variant-picker[data-product-id="${productId}"]`);
  const sticky = html.querySelector(`sticky-add-to-cart[data-product-id="${productId}"]`);
  const variantTitleEl = sticky?.querySelector('.sticky-add-to-cart__variant');

  if (!picker || !(variantTitleEl instanceof HTMLElement)) return;

  patchRegionLabels(picker, productId);

  const parts = getVariantTitleParts(picker);
  if (parts.length) variantTitleEl.textContent = parts.join(' / ');
}

/**
 * @param {Element} variantPicker
 * @returns {string[]}
 */
function getVariantTitleParts(variantPicker) {
  /** @type {string[]} */
  const parts = [];

  variantPicker
    .querySelectorAll(':scope .variant-picker__form > fieldset.variant-option, :scope .variant-picker__form > .variant-option')
    .forEach((container) => {
      const part = getOptionDisplayPart(container);
      if (part) parts.push(part);
    });

  return parts;
}

/**
 * @param {Element} container
 * @returns {string | null}
 */
function getOptionDisplayPart(container) {
  const tabs = container.querySelector('[data-size-region-tabs]');

  if (container.matches('fieldset')) {
    const checked = container.querySelector('input:checked');
    if (!(checked instanceof HTMLInputElement)) return null;

    const labelEl = checked.closest('label')?.querySelector('[data-key="variant-option-text"]');
    return formatRegionLabel(tabs, labelEl, checked.value);
  }

  const select = container.querySelector('select');
  if (!(select instanceof HTMLSelectElement)) return null;

  const selected = select.options[select.selectedIndex];
  if (!selected) return null;

  return formatRegionLabel(tabs, selected, selected.value, true);
}

/**
 * @param {Element | null} tabs
 * @param {Element | null} labelEl
 * @param {string} fallbackValue
 * @param {boolean} [readFromDataAttr]
 * @returns {string | null}
 */
function formatRegionLabel(tabs, labelEl, fallbackValue, readFromDataAttr = false) {
  if (!tabs) return fallbackValue;

  const activeTab = tabs.querySelector('[data-size-region].is-active');
  const region = activeTab?.dataset.sizeRegion;
  const regionPrefix = activeTab?.textContent?.trim();

  let value = fallbackValue;

  if (readFromDataAttr && labelEl instanceof HTMLOptionElement && region) {
    value = labelEl.getAttribute(`data-size-label-${region}`) || fallbackValue;
  } else if (labelEl?.textContent) {
    value = labelEl.textContent.trim();
  }

  if (regionPrefix && value) {
    return `${regionPrefix}: ${value}`;
  }

  return fallbackValue;
}

/**
 * @param {Element} variantPicker
 */
function updateStickyVariantTitle(variantPicker) {
  const productId = variantPicker.dataset.productId;
  if (!productId || !variantPicker.querySelector('[data-size-region-tabs]')) return;

  const stickyBar = document.querySelector(`sticky-add-to-cart[data-product-id="${productId}"]`);
  const variantTitleEl = stickyBar?.querySelector('.sticky-add-to-cart__variant');
  if (!(variantTitleEl instanceof HTMLElement)) return;

  const parts = getVariantTitleParts(variantPicker);
  if (parts.length) variantTitleEl.textContent = parts.join(' / ');
}

document.addEventListener('click', (event) => {
  const tab = event.target.closest('[data-size-region]');
  if (!tab) return;

  const tabs = tab.closest('[data-size-region-tabs]');
  const container = tabs?.closest('fieldset, .variant-option--has-region-tabs');
  if (!tabs || !container) return;

  const region = tab.dataset.sizeRegion;
  if (!region) return;

  const variantPicker = container.closest('variant-picker');
  const productId = variantPicker?.dataset.productId;
  if (productId) activeRegions.set(productId, region);

  applyRegion(tabs, region);

  if (variantPicker) updateStickyVariantTitle(variantPicker);
});

document.addEventListener(
  ThemeEvents.variantUpdate,
  (event) => {
    const productId = event.detail?.data?.productId;
    const html = event.detail?.data?.html;
    if (!productId || !(html instanceof Document)) return;

    patchStickyVariantInHtml(html, productId);
  },
  true
);

document.querySelectorAll('variant-picker').forEach((picker) => {
  if (picker.querySelector('[data-size-region-tabs]')) {
    updateStickyVariantTitle(picker);
  }
});
